import { formatDispenseQuantity } from '../utilities/productDisplay';

const DURATIONS = [1, 3, 6];

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 'TBD';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const normalizeProducts = (products = []) => {
  return products
    .filter(product => product?.product_name && product?.price != null)
    .sort((a, b) => String(a.product_code || '').localeCompare(String(b.product_code || ''), undefined, { numeric: true }));
};

const getDurationsForProduct = (product) => {
  if (Array.isArray(product?.supportedDurations) && product.supportedDurations.length > 0) {
    return product.supportedDurations.map(Number);
  }

  if (Array.isArray(product?.supported_durations) && product.supported_durations.length > 0) {
    return product.supported_durations.map(Number);
  }

  return DURATIONS;
};

const getDisplayedDurations = (products) => {
  const durations = new Set();
  products.forEach(product => getDurationsForProduct(product).forEach(duration => durations.add(Number(duration))));
  return DURATIONS.filter(duration => durations.has(duration));
};

const getPriceForDuration = (product, duration) => {
  return getDurationsForProduct(product).includes(duration)
    ? formatCurrency(Number(product.price) * duration)
    : 'Not offered';
};

const groupProductsByName = (products) => {
  const normalized = normalizeProducts(products);
  const order = [];
  const groups = new Map();

  normalized.forEach(product => {
    const name = String(product.product_name);
    if (!groups.has(name)) {
      groups.set(name, []);
      order.push(name);
    }
    groups.get(name).push(product);
  });

  return order.map(name => ({ name, products: groups.get(name) }));
};

function PricingTable({ title, products }) {
  const displayedDurations = getDisplayedDurations(products);
  const groupDirections = products.find(product => product.directions)?.directions || '';
  const groupRoute = products.find(product => product.route)?.route || '';
  const groupCaption = [groupRoute, groupDirections].filter(Boolean).join(' — ');

  return (
    <div className="w-full">
      <h3 className="text-xl font-semibold text-primary mb-3">{title}</h3>
      {groupCaption && <p className="text-sm text-neutral-dark mb-3">{groupCaption}</p>}
      <div className="grid grid-cols-1 gap-3 md:hidden">
        {products.map(product => {
          const monthlyQuantity = formatDispenseQuantity(product, 1);
          return (
          <div key={product.product_code || `${product.product_name}-${product.dosage}`} className="border border-gray-200 rounded bg-white p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="font-semibold text-primary">{product.dosage || product.displayname}</p>
                {monthlyQuantity && <p className="text-xs text-neutral-dark">{monthlyQuantity}/month</p>}
                <p className="text-xs text-neutral-dark">{product.directions || groupDirections}</p>
              </div>
            </div>
            <dl className="grid grid-cols-3 gap-2 text-sm">
              {displayedDurations.map(duration => (
                <div key={duration} className="rounded bg-neutral-light p-2">
                  <dt className="text-xs text-gray-600">{duration} mo</dt>
                  <dd className="font-semibold text-neutral-dark">{getPriceForDuration(product, duration)}</dd>
                </div>
              ))}
            </dl>
          </div>
          );
        })}
      </div>
      <div className="hidden md:block overflow-x-auto border border-gray-200 rounded">
        <table className="w-full min-w-[36rem] text-sm bg-white">
          <thead className="bg-neutral-light text-primary-dark">
            <tr>
              <th className="text-left p-3 font-semibold">Dose</th>
              <th className="text-left p-3 font-semibold">Dispensed / month</th>
              {displayedDurations.map(duration => (
                <th key={duration} className="text-right p-3 font-semibold">{duration} month{duration > 1 ? 's' : ''}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.product_code || `${product.product_name}-${product.dosage}`} className="border-t border-gray-200">
                <td className="p-3 font-medium text-neutral-dark whitespace-nowrap">{product.dosage || product.displayname}</td>
                <td className="p-3 text-neutral-dark whitespace-nowrap">{formatDispenseQuantity(product, 1) || '—'}</td>
                {displayedDurations.map(duration => (
                  <td key={duration} className="p-3 text-right text-neutral-dark whitespace-nowrap">
                    {getPriceForDuration(product, duration)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PricingDisplay({ products = [] }) {
  const groups = groupProductsByName(products);

  if (!groups.length) {
    return (
      <p className="text-neutral-dark text-center">
        No pricing information available at this time.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {groups.map(group => (
        <PricingTable key={group.name} title={group.name} products={group.products} />
      ))}
    </div>
  );
}

export default PricingDisplay;
